import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import api, { apiError } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import { CommentThread } from "@/components/CommentThread";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ListChecks, Plus, Loader2, Trash2, Flag, MessageSquare, Search, Euro, Clock, Pencil } from "lucide-react";
import { CashToggle } from "@/components/CashToggle";
import { TaskEditDialog } from "@/components/TaskEditDialog";
import { formatDate } from "@/lib/date";
import { toast } from "sonner";

import { tx, lz } from "@/i18n";
import { useOriginals, OrigToggle } from "@/components/OrigToggle";
const empty = { house_id: "", title: "", description: "", category: "", assignee_id: "", due_date: "", due_time: "", priority: "normal", status: "open", extra_cost: "", cash: false };
const PRIO = lz({ low: "Laag", normal: "Normaal", high: "Hoog" });
const euro = (v) => Number(v).toLocaleString("nl-NL", { style: "currency", currency: "EUR" });

export default function Tasks() {
  const orig = useOriginals();
  const { user } = useAuth();
  const { data: notif, refresh: refreshNotif } = useNotifications();
  const [tasks, setTasks] = useState(null);
  const [houses, setHouses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [cats, setCats] = useState([]);
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [commentTask, setCommentTask] = useState(null);
  const [costTask, setCostTask] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [costVal, setCostVal] = useState("");
  const [costSaving, setCostSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [hoursEdit, setHoursEdit] = useState({});
  const [workerEdit, setWorkerEdit] = useState({});
  const canManage = ["admin"].includes(user.role);

  const load = () => api.get("/tasks").then(({ data }) => setTasks(data)).catch(() => setTasks([]));
  useEffect(() => {
    load();
    api.get("/task-categories").then(({ data }) => setCats(data)).catch(() => {});
    if (canManage) {
      api.get("/houses").then(({ data }) => setHouses(data)).catch(() => {});
      api.get("/users").then(({ data }) => setStaff(data.filter((u) => ["cleaner", "subcontractor", "host"].includes(u.role)))).catch(() => {});
    }
  }, []);

  // Load effective checklist preview + default assignee when house+category chosen
  useEffect(() => {
    if (!open || !form.category || !form.house_id) { setPreview(null); return; }
    api.get(`/houses/${form.house_id}/task-config`).then(({ data }) => {
      const cfg = data[form.category];
      setPreview(cfg?.subtasks || []);
      if (cfg?.default_assignee_id && !form.assignee_id) setForm((f) => ({ ...f, assignee_id: cfg.default_assignee_id }));
    }).catch(() => setPreview(null));
    // eslint-disable-next-line
  }, [form.category, form.house_id, open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const catLabel = (key) => cats.find((c) => c.key === key)?.label;

  const save = async () => {
    setSaving(true);
    try {
      const cost = form.extra_cost === "" || form.extra_cost == null ? null : Number(form.extra_cost);
      if (form.category) {
        await api.post("/tasks/from-template", {
          house_id: form.house_id || null, category_key: form.category,
          assignee_id: form.assignee_id || null, due_date: form.due_date || null, due_time: form.due_time || null, extra_cost: cost, cash: form.cash,
        });
      } else {
        await api.post("/tasks", { ...form, category: null, house_id: form.house_id || null, assignee_id: form.assignee_id || null, due_date: form.due_date || null, due_time: form.due_time || null, extra_cost: cost });
      }
      toast.success(tx("Taak aangemaakt"));
      setOpen(false); setForm(empty); setPreview(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const toggle = async (t) => {
    try { await api.patch(`/tasks/${t.id}/status`, null, { params: { status: t.status === "done" ? "open" : "done" } }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const del = async (id) => { try { await api.delete(`/tasks/${id}`); load(); } catch (err) { toast.error(apiError(err)); } };
  const toggleCash = async (id, current) => { try { await api.patch(`/tasks/${id}/cash`, null, { params: { cash: !current } }); load(); } catch (err) { toast.error(apiError(err)); } };
  const canEditHours = (t) => canManage || ["host", "cleaner", "subcontractor"].includes(user.role);
  const staffWithRate = staff.filter((s) => s.hourly_rate != null && s.hourly_rate !== "");
  const saveHours = async (t, wbOverride) => {
    const rawH = hoursEdit[t.id] ?? (t.worked_hours ?? "");
    const wb = wbOverride !== undefined ? wbOverride : (workerEdit[t.id] ?? (t.worked_by || t.assignee_id || ""));
    const v = rawH === "" ? null : Number(rawH);
    if (v != null && (isNaN(v) || v < 0)) return;
    const params = {};
    if (v != null) params.hours = v;
    if (wb) params.worked_by = wb;
    try { await api.patch(`/tasks/${t.id}/hours`, null, { params }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const openCost = (t) => { setCostTask(t); setCostVal(t.extra_cost != null ? String(t.extra_cost) : ""); };
  const saveCost = async () => {
    setCostSaving(true);
    try {
      const cost = costVal === "" || costVal == null ? null : Number(costVal);
      await api.patch(`/tasks/${costTask.id}`, { extra_cost: cost });
      toast.success(tx("Extra kosten bijgewerkt"));
      setCostTask(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setCostSaving(false); }
  };
  const toggleSub = async (taskId, index, done) => {
    try { await api.patch(`/tasks/${taskId}/subtask`, null, { params: { index, done } }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const houseName = (hid) => houses.find((h) => h.id === hid)?.name;
  const assigneeName = (aid) => staff.find((s) => s.id === aid)?.name;

  const q = search.trim().toLowerCase();
  const looseTasks = tasks ? tasks.filter((t) => !t.reservation_id) : tasks;
  const filteredTasks = !looseTasks ? looseTasks : (q ? looseTasks.filter((t) => [t.title, t.description, catLabel(t.category), houseName(t.house_id), t.house_name, assigneeName(t.assignee_id), t.assignee_name].filter(Boolean).join(" ").toLowerCase().includes(q)) : looseTasks);

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">{tx("Taken")}</h1>
          <p className="text-muted-foreground mt-1">{tx("Toegewezen werkzaamheden voor het team.")}</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-task-btn"><Plus className="h-4 w-4" /> {tx("Taak")}</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display">{tx("Nieuwe taak")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>{tx("Categorie (sjabloon)")}</Label>
                  <Select value={form.category} onValueChange={set("category")}>
                    <SelectTrigger className="mt-1" data-testid="task-category-select"><SelectValue placeholder={tx("Optioneel — laadt checklist uit sjabloon")} /></SelectTrigger>
                    <SelectContent>{cats.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {!form.category && (
                  <>
                    <div><Label>{tx("Titel *")}</Label><Input value={form.title} onChange={set("title")} className="mt-1" data-testid="task-title-input" /></div>
                    <div><Label>{tx("Beschrijving")}</Label><Textarea value={form.description} onChange={set("description")} className="mt-1" /></div>
                  </>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{tx("Woning")}</Label>
                    <Select value={form.house_id} onValueChange={set("house_id")}>
                      <SelectTrigger className="mt-1" data-testid="task-house-select"><SelectValue placeholder={tx("Optioneel")} /></SelectTrigger>
                      <SelectContent>{houses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{tx("Prioriteit")}</Label>
                    <Select value={form.priority} onValueChange={set("priority")}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(PRIO).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{tx("Toegewezen aan")}</Label>
                    <Select value={form.assignee_id} onValueChange={set("assignee_id")}>
                      <SelectTrigger className="mt-1" data-testid="task-assignee-select"><SelectValue placeholder={tx("Kies")} /></SelectTrigger>
                      <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {ROLE_LABELS[s.role]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>{tx("Datum")}</Label><DatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} placeholder={tx("Geen datum")} className="mt-1" testId="task-due-date-input" /></div>
                </div>
                {form.due_date && (
                  <div>
                    <Label>{tx("Tijd")}</Label>
                    <Input type="time" value={form.due_time} onChange={set("due_time")} className="mt-1 w-32" data-testid="task-due-time-input" />
                    <p className="text-xs text-muted-foreground mt-1">{tx("Met woning en datum verschijnt de taak ook in de Agenda (standaard 09:00).")}</p>
                  </div>
                )}
                <div>
                  <Label>{tx("Extra kosten (€)")}</Label>
                  <Input type="number" min="0" step="0.01" value={form.extra_cost} onChange={set("extra_cost")} placeholder={tx("Optioneel — bijv. materiaal/reparatie")} className="mt-1" data-testid="task-extra-cost-input" />
                </div>
                <div className="flex justify-end pt-1">
                  <CashToggle value={form.cash} canEdit onToggle={() => set("cash")(!form.cash)} testId="task-cash-toggle" />
                </div>
                {form.category && (
                  <div className="rounded-lg border border-border bg-muted/40 p-3" data-testid="template-preview">
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">
                      {tx("Checklist uit sjabloon")}{form.house_id ? tx(" (van deze woning)") : ""}:
                    </p>
                    {preview === null ? (
                      <p className="text-xs text-muted-foreground">{tx("Kies een woning om de checklist van dat huis te laden.")}</p>
                    ) : preview.length === 0 ? (
                      <p className="text-xs text-muted-foreground">{tx("Geen subtaken in dit sjabloon.")}</p>
                    ) : (
                      <ul className="text-sm space-y-0.5 list-disc list-inside">{preview.map((s, i) => <li key={i}>{s}</li>)}</ul>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={save} disabled={saving || (!form.category && !form.title)} className="bg-primary hover:bg-primary/90" data-testid="save-task-btn">
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Opslaan")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {tasks && tasks.length > 0 && (
        <div className="relative mb-4 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tx("Zoek op taak, woning of medewerker…")} className="pl-9" data-testid="tasks-search" />
        </div>
      )}

      {!tasks ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : tasks.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground">
          <ListChecks className="h-10 w-10 mx-auto mb-3 opacity-40" /> {tx("Geen taken.")}
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground" data-testid="tasks-no-results">
          <Search className="h-10 w-10 mx-auto mb-3 opacity-40" /> {tx("Geen taken gevonden voor \"")}{search}".
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTasks.map((t) => (
            <div key={t.id} className="bg-card rounded-xl border border-border p-5 shadow-sm flex flex-wrap sm:flex-nowrap items-start gap-x-4 gap-y-2" data-testid={`task-${t.id}`}>
              <Checkbox checked={t.status === "done"} onCheckedChange={() => toggle(t)} className="mt-1" data-testid={`task-check-${t.id}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={`font-display font-semibold ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{orig.show(t, "title")}</h3>
                  {t.category && catLabel(t.category) && <span className="text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-1.5 py-0.5 rounded font-semibold" data-testid={`task-category-${t.id}`}>{catLabel(t.category)}</span>}
                  {t.priority === "high" && <span className="flex items-center gap-1 text-xs font-semibold text-destructive"><Flag className="h-3 w-3" /> {tx("Hoog")}</span>}
                  {t.supply_done && <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold" title={`${tx("Geregeld door")} ${t.supply_done_by || ""}`} data-testid={`task-supply-done-${t.id}`}>{tx("Geregeld")}</span>}
                  <CashToggle value={t.cash} canEdit={canManage} onToggle={() => toggleCash(t.id, t.cash)} testId={`task-cash-${t.id}`} />
                </div>
                {t.description && <p className="text-sm text-muted-foreground mt-1">{orig.show(t, "description")}</p>}
                <OrigToggle t={t} o={orig} />
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2 flex-wrap">
                  {houseName(t.house_id) && <span>🏠 {houseName(t.house_id)}</span>}
                  {(assigneeName(t.assignee_id) || t.assignee_name) && <span>👤 {assigneeName(t.assignee_id) || t.assignee_name}</span>}
                  {t.due_date && <span data-testid={`task-due-${t.id}`}>📅 {formatDate(t.due_date)}{t.due_time ? ` · ${t.due_time}` : ""}</span>}
                  {t.extra_cost != null && t.extra_cost !== "" && <span className="inline-flex items-center gap-1 font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded" data-testid={`task-cost-${t.id}`}>💶 {euro(t.extra_cost)}</span>}
                  {canEditHours(t) ? (
                    <span className="inline-flex items-center gap-1.5" data-testid={`task-hours-wrap-${t.id}`}>
                      <Clock className="h-3.5 w-3.5" />
                      <input
                        type="number" min="0" step="0.25"
                        value={hoursEdit[t.id] ?? (t.worked_hours ?? "")}
                        onChange={(e) => setHoursEdit((p) => ({ ...p, [t.id]: e.target.value }))}
                        onBlur={() => saveHours(t)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                        placeholder={tx("uren")}
                        className="w-16 h-6 rounded border border-input bg-background px-1.5 text-xs"
                        data-testid={`task-hours-input-${t.id}`}
                      /> {tx("u")}
                      {canManage && (
                        <select
                          value={workerEdit[t.id] ?? (t.worked_by || t.assignee_id || "")}
                          onChange={(e) => { setWorkerEdit((p) => ({ ...p, [t.id]: e.target.value })); saveHours(t, e.target.value); }}
                          className="h-6 rounded border border-input bg-background px-1 text-xs max-w-[150px]"
                          data-testid={`task-worker-select-${t.id}`}
                          title={tx("Wie heeft deze uren gewerkt (voor loonkosten)")}>
                          <option value="">{tx("— medewerker —")}</option>
                          {staffWithRate.map((s) => <option key={s.id} value={s.id}>{`${s.name} (€${s.hourly_rate})`}</option>)}
                        </select>
                      )}
                    </span>
                  ) : (t.worked_hours != null && <span className="inline-flex items-center gap-1" data-testid={`task-hours-${t.id}`}><Clock className="h-3.5 w-3.5" /> {t.worked_hours} u</span>)}
                </div>
                {t.subtasks && t.subtasks.length > 0 && (
                  <div className="mt-3 space-y-1.5 border-t border-border pt-3">
                    {t.subtasks.map((s, i) => (
                      <label key={i} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`subtask-${t.id}-${i}`}>
                        <Checkbox checked={!!s.done} onCheckedChange={(v) => toggleSub(t.id, i, !!v)} className="h-4 w-4" />
                        <span className={s.done ? "line-through text-muted-foreground" : ""}>{orig.sub(t, s)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0 max-sm:basis-full max-sm:justify-end">
                <Button size="sm" variant="ghost" onClick={() => setCommentTask(t)} className="gap-1.5 relative text-muted-foreground" data-testid={`task-comments-${t.id}`}>
                  <MessageSquare className="h-4 w-4" />
                  {notif.tasks?.[t.id] > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 grid place-items-center min-w-[16px] h-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold" data-testid={`task-unread-${t.id}`}>{notif.tasks[t.id]}</span>
                  )}
                </Button>
                {canManage && <Button size="icon" variant="ghost" onClick={() => setEditTask(t)} className="text-muted-foreground hover:text-primary" title={tx("Taak bewerken")} data-testid={`task-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>}
                {canManage && <Button size="icon" variant="ghost" onClick={() => openCost(t)} className="text-muted-foreground hover:text-emerald-700" title={tx("Extra kosten bewerken")} data-testid={`task-edit-cost-${t.id}`}><Euro className="h-4 w-4" /></Button>}
                {canManage && <Button size="icon" variant="ghost" onClick={() => del(t.id)} className="text-destructive" data-testid={`task-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {canManage && <TaskEditDialog task={editTask} houses={houses} staff={staff} onClose={() => setEditTask(null)} onSaved={() => { setEditTask(null); load(); }} />}
      <Dialog open={!!commentTask} onOpenChange={(o) => !o && setCommentTask(null)}>
        <DialogContent data-testid="task-comments-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><MessageSquare className="h-5 w-5 text-primary" /> {tx("Opmerkingen ·")} {commentTask?.title}</DialogTitle><DialogDescription className="sr-only">{tx("Opmerkingen bij deze taak")}</DialogDescription></DialogHeader>
          {commentTask && <CommentThread entityType="task" entityId={commentTask.id} onChange={refreshNotif} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!costTask} onOpenChange={(o) => !o && setCostTask(null)}>
        <DialogContent className="max-w-sm" data-testid="task-cost-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Euro className="h-5 w-5 text-emerald-700" /> {tx("Extra kosten")}</DialogTitle>
            <DialogDescription>{costTask?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>{tx("Bedrag (€)")}</Label>
            <Input type="number" min="0" step="0.01" value={costVal} onChange={(e) => setCostVal(e.target.value)} placeholder={tx("Leeg = geen kosten")} data-testid="task-cost-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCostTask(null)} data-testid="task-cost-cancel">{tx("Annuleren")}</Button>
            <Button onClick={saveCost} disabled={costSaving} className="bg-primary hover:bg-primary/90" data-testid="task-cost-save">
              {costSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {tx("Opslaan")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
